import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../../components/Badge';
import { Button } from '../../../../components/Button';
import { Input } from '../../../../components/forms/Input';
import { Modal } from '../../../../components/Modal';
import {
  useCreateSchedulingProfile,
  useDeactivateSchedulingProfile,
  useDeleteSchedulingProfile,
  useSchedulingAccess,
  useSchedulingProfiles,
} from '../hooks';
import { isProtectedByBookings, SchedulingApiError, SchedulingProfile } from '../types';
import { ShowInactiveToggle } from './ShowInactiveToggle';

export const SchedulingCalendarsSection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: access } = useSchedulingAccess();
  const enabled = access?.enabled ?? false;
  const { data: profiles = [], isLoading } = useSchedulingProfiles(enabled);
  const createProfile = useCreateSchedulingProfile();
  const deleteProfile = useDeleteSchedulingProfile();
  const deactivateProfile = useDeactivateSchedulingProfile();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<SchedulingProfile | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // Shown only for tiers with scheduling; Basic keeps the external-only view.
  if (!enabled) return null;

  const inactiveCount = profiles.filter(profile => !profile.is_active).length;
  const visibleProfiles = showInactive ? profiles : profiles.filter(profile => profile.is_active);

  const openCreate = () => {
    setName('');
    setFieldErrors({});
    setFormError(null);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setFieldErrors({});
    setFormError(null);
    try {
      const profile = await createProfile.mutateAsync({ name: name.trim() });
      setCreateOpen(false);
      navigate(`/calendars/${profile.id}`);
    } catch (error) {
      if (error instanceof SchedulingApiError) {
        setFieldErrors(error.fieldErrors);
        setFormError(error.nonFieldErrors[0] ?? null);
      } else {
        setFormError(t('schedulingAdmin.errors.generic'));
      }
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleteError(null);
    try {
      await deleteProfile.mutateAsync(toDelete.id);
      setToDelete(null);
    } catch (error) {
      if (isProtectedByBookings(error)) {
        setDeleteBlocked(true);
      } else {
        setDeleteError(
          error instanceof SchedulingApiError ? error.message : t('schedulingAdmin.errors.generic')
        );
      }
    }
  };

  const handleDeactivate = async () => {
    if (!toDelete) return;
    setDeleteError(null);
    try {
      await deactivateProfile.mutateAsync(toDelete.id);
      setToDelete(null);
    } catch {
      setDeleteError(t('schedulingAdmin.errors.generic'));
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('schedulingAdmin.list.title')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('schedulingAdmin.list.subtitle')}
          </p>
        </div>
        <Button onClick={openCreate}>{t('schedulingAdmin.list.create')}</Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : profiles.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('schedulingAdmin.list.empty')}
        </p>
      ) : (
        <div className="space-y-4">
          <ShowInactiveToggle
            count={inactiveCount}
            checked={showInactive}
            onChange={setShowInactive}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleProfiles.map(profile => (
              <div
                key={profile.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                      {profile.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{profile.timezone}</p>
                  </div>
                  <Badge variant="info" size="sm">
                    {t('schedulingAdmin.list.typeBadge')}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={profile.is_active ? 'success' : 'secondary'} size="sm">
                    {profile.is_active ? t('common.active') : t('common.inactive')}
                  </Badge>
                  {profile.booking_page_enabled && (
                    <Badge variant="outline" size="sm">
                      {t('schedulingAdmin.list.bookingPageOn')}
                    </Badge>
                  )}
                  {profile.sync_calendar && (
                    <Badge variant="outline" size="sm">
                      {t('schedulingAdmin.list.syncedTo', {
                        name: profile.sync_calendar.name || profile.sync_calendar.provider_display,
                      })}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {profile.agents.length === 0 ? (
                    t('schedulingAdmin.list.usedByNone')
                  ) : (
                    <>
                      {t('schedulingAdmin.list.usedBy')}{' '}
                      {profile.agents.map((agent, index) => (
                        <React.Fragment key={agent.id}>
                          {index > 0 && ', '}
                          <Link
                            to={`/agents/edit/${agent.id}`}
                            className="text-blue-600 hover:underline dark:text-blue-400"
                          >
                            {agent.name}
                          </Link>
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </p>
                <div className="flex gap-2 mt-auto pt-1">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => navigate(`/appointments/${profile.id}`)}
                  >
                    {t('schedulingAdmin.list.openAppointments')}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate(`/calendars/${profile.id}`)}
                  >
                    {t('schedulingAdmin.list.setup')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    title={t('common.delete')}
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteBlocked(false);
                      setToDelete(profile);
                    }}
                  >
                    <span className="material-icons text-lg">delete</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('schedulingAdmin.list.createTitle')}
        size="md"
      >
        <div className="space-y-4">
          <Input
            label={t('schedulingAdmin.fields.name')}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('schedulingAdmin.list.namePlaceholder')}
            errorText={fieldErrors.name}
            state={fieldErrors.name ? 'error' : 'default'}
            fullWidth
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('schedulingAdmin.fields.timezone')}
            </label>
            <p className="text-sm text-gray-900 dark:text-gray-100">{access?.timezone}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('schedulingAdmin.settings.timezoneFromOrganization')}
            </p>
          </div>
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              loading={createProfile.isPending}
              disabled={!name.trim()}
            >
              {t('common.create')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        title={t('schedulingAdmin.list.deleteTitle')}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {deleteBlocked
              ? t('schedulingAdmin.errors.protectedByBookings', { name: toDelete?.name })
              : t('schedulingAdmin.list.deleteText', { name: toDelete?.name })}
          </p>
          {deleteBlocked && toDelete?.is_active && toDelete.agents.length > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('schedulingAdmin.errors.deactivateUnassigns', {
                agents: toDelete.agents.map(agent => agent.name).join(', '),
              })}
            </p>
          )}
          {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setToDelete(null)}>
              {t('common.cancel')}
            </Button>
            {!deleteBlocked ? (
              <Button variant="danger" onClick={handleDelete} loading={deleteProfile.isPending}>
                {t('common.delete')}
              </Button>
            ) : (
              toDelete?.is_active && (
                <Button onClick={handleDeactivate} loading={deactivateProfile.isPending}>
                  {t('schedulingAdmin.errors.deactivateInstead')}
                </Button>
              )
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};
